import styled from '@emotion/styled';
import bbox from '@turf/bbox';
import { combine, featureCollection, point } from '@vromo/geojson';
import useStylesheets from '@vromo/hooks/useStylesheets';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import some from 'lodash/some';
import mapboxgl from 'mapbox-gl';
import { autorun, runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';

import { mapboxStyles, secrets } from '@vromo/api';
import MapboxStyle from 'mapbox-gl/dist/mapbox-gl.css';
import { qaLabelTrackingLink } from '../../../common-qa-labels/tracking-link';
import { useStore } from '@vromo/mobx-react/mobx-react';
import TrackingLinkStore from '../TrackingLinkStore';

const Container = styled.div`
    flex: 1;
    background-color: white;
    position: relative;
    width: ${({ width }) => width}px;
    height: ${({ height }) => height}px;
`;

const DEFAULT_SOURCE = {
    type: 'geojson',
    data: {
        type: 'FeatureCollection',
        features: []
    }
};

interface TrackingMapProps {
    width: number;
    height: number;
    icon?: string;
}

const TrackingMap = observer<TrackingMapProps>(({ width, height, icon }) => {
    useStylesheets(MapboxStyle);

    const rootStore = useStore<TrackingLinkStore>('rootStore');
    const jobs = rootStore.jobs;
    const job = jobs.first()!;
    const vehicles = rootStore.vehicles;
    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const state = useLocalObservable(() => ({ tracking: true, scale: 0.5 }));

    const fitBounds = (linear = false, userDidntInteract = false) => {
        if (!mapRef?.current) return;
        const map = mapRef.current as mapboxgl.Map;
        map.removeLayer;
        const bounds = bbox(combine(map.getSource('jobs')._data, map.getSource('vehicles')._data));

        if (!some(bounds, (it) => it === Infinity)) {
            map.fitBounds(bounds, { linear, padding: 80 }, { userDidntInteract });
        }
    };

    useEffect(() => {
        if (mapRef.current) mapRef.current.resize();
    }, [width, height]);

    useEffect(() => {
        mapboxgl.accessToken = secrets.mapbox;

        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapboxStyles.trackmap
        });

        mapRef.current = map;

        map.on('load', () => {
            map.on('move', (e) => {
                if (!e.userDidntInteract && state.tracking) {
                    runInAction(() => (state.tracking = false));
                }
            });

            map.addSource('jobs', DEFAULT_SOURCE);
            map.addSource('vehicles', DEFAULT_SOURCE);

            map.addLayer({
                id: 'jobs-layer',
                type: 'symbol',
                source: 'jobs',
                layout: {
                    'icon-image': '{icon}',
                    'icon-size': 0.6,
                    'icon-allow-overlap': true,
                    'text-anchor': 'bottom',
                    'icon-anchor': 'bottom',
                    'text-offset': [0, 2.7],
                    'text-size': 12,
                    'text-field': '{label}',
                    'text-optional': true
                }
            });

            map.addLayer({
                id: 'vehicles-layer',
                type: 'symbol',
                source: 'vehicles',
                layout: {
                    'icon-image': '{icon}',
                    'icon-size': ['number', ['get', 'size'], 0.5],
                    'icon-rotate': ['number', ['get', 'heading'], 0],
                    'icon-allow-overlap': true
                }
            });

            map.loadImage(require('./assets/store_marker.png'), (err, image) => {
                map.addImage('pickup', image);
            });

            map.loadImage(require('./assets/home_marker.png'), (err, image) => {
                map.addImage('dropoff', image);
            });

            if (icon) {
                map.loadImage(icon, (err, image) => {
                    if (err) {
                        map.loadImage(require('@vromo/icons/car/highlighted.png'), (err, image) => {
                            map.addImage('vehicle', image);
                        });
                    } else {
                        map.addImage('vehicle', image);

                        runInAction(() => {
                            state.scale = Math.min(
                                (100 / image.width) * 0.5,
                                (100 / image.height) * 0.5
                            );
                        });
                    }
                });
            } else {
                map.loadImage(require('@vromo/icons/car/highlighted.png'), (err, image) => {
                    map.addImage('vehicle', image);
                });
            }

            return autorun(() => {
                map.getSource('vehicles').setData(
                    featureCollection(
                        job.status === 'underway'
                            ? vehicles
                                  .filter((it) => it.location?.lon && it.location?.lat)
                                  .map((it) =>
                                      point([it.location.lon, it.location.lat], {
                                          icon: 'vehicle',
                                          heading: it.point.heading,
                                          size: state.scale
                                      })
                                  )
                            : []
                    )
                );

                map.getSource('jobs').setData(
                    combine(
                        ...jobs.map((job) =>
                            featureCollection(
                                job.tasks
                                    .filter((t) => t.ix >= job.progress - 1)
                                    .map((task, ix) => {
                                        const { zone } = task;
                                        const addressParts = zone?.address?.split(',') || [
                                            ix === 0 ? 'pickup' : 'dropoff'
                                        ];
                                        const label = addressParts[0];
                                        if (job.finished) {
                                            return point([zone.coords.lon, zone.coords.lat], {
                                                label,
                                                icon: 'dropoff'
                                            });
                                        }
                                        return point([zone.coords.lon, zone.coords.lat], {
                                            label,
                                            icon: ix === 0 ? 'pickup' : 'dropoff'
                                        });
                                    })
                            )
                        )
                    )
                );

                if (state.tracking) fitBounds(true, true);
            });
        });
    }, []);

    return (
        <Container data-testid={qaLabelTrackingLink.map} {...{ width, height }}>
            <div ref={mapContainerRef} style={{ width, height }} />
            {!state.tracking && (
                <div
                    style={{
                        padding: 6,
                        borderRadius: '50%',
                        lineHeight: 1,
                        backgroundColor: 'black',
                        color: 'white',
                        cursor: 'pointer',
                        position: 'absolute',
                        top: 10,
                        right: 10
                    }}
                    onClick={() => {
                        fitBounds(false, true);
                        runInAction(() => {
                            state.tracking = true;
                        });
                    }}>
                    <FontAwesome icon="crosshairs" />
                </div>
            )}
        </Container>
    );
});

export default TrackingMap;
